import { isLocalhost } from 'app/lib/utils/UiUtils';
import Button from 'components/shared/app/Button';
import Logo from 'components/shared/app/Logo';
import Spinner from 'components/shared/app/Spinner';
import Config from 'lib/Config';
import Constants from 'lib/Constants';
import GoogleService from 'lib/services/providers/GoogleService';
import { getAccountProviders, getInnerError } from 'lib/utils/adaya/AdayaUtils';
import { ignoreProviderForDev } from 'lib/utils/adaya/ProviderUtils';
import { getLogger } from 'lib/utils/logging';
import { ProviderNames } from 'models/Providers';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FacebookService from 'services/providers/FacebookService';
import arrowIcon from 'static/icons/icon_arrow_blue_round.svg';
import { getNormalizedUrlParams } from 'utils/BrowserUtils';
import './style.scss';

const { log, warn } = getLogger('Login', { enabled: false });

export default Login = (props) => {
    const location = useLocation();
    const navigate = useNavigate();

    const params = getNormalizedUrlParams();
    const redirect = params.get('redirect');

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingProvider, setLoadingProvider] = useState(false);
    const [showDisabled, setShowDisabled] = useState(false);

    const [loggingInto, setLoggingInto] = useState('account');
    const [needsProviderLogin, setNeedsProviderLogin] = useState(false);

    const user = props.auth.user;
    const inputEmail = React.createRef();
    const inputPassword = React.createRef();

    useEffect(async () => {
        console.log('Login page:', props, props.auth)
    }, []);

    // Redirect if logged in...

    useEffect(async () => {
        if (props.auth.authFinished && props.auth.loggedIn && props.auth.user) {
            log(`Logged in basic.`, props.auth, redirect)
            const user = props.auth.user;
            const url = (redirect || Constants.DEFAULT_LOGIN_ROUTE);

            if (props.auth.loggedInProviders) {
                log("Logged in providers, navigating to: ", url);
                debug(user, 'User is logged in (login page).');
                // if all logged in, go to route
                return navigate(url);
            } else {
                await checkProviderAuths(url);
            }
        } else {
            console.log(`User not logged in.`, props.auth.authFinished, props.auth.loggedIn, { redirect });
        }
    }, [props.auth.authFinished, props.auth.loggedIn, props.auth.loggedInProviders, props.auth.user]);

    checkProviderAuths = async (redirectUrl) => {
        // TODO: This just handles FB really, has to be rewritten for all generic providers..

        // if (FacebookService.useMockAccessToken()) {
        //     setLoggingInto('');
        //     console.log(`Using mock FB token, setting providers logged in.`)
        //     // todo: need to do this
        //     return props.auth.setProvidersLoggedIn(true);
        // }

        let providers = getAccountProviders(user.activeAccount, [ProviderNames.ADAYA, ProviderNames.SHOPIFY]);

        const checkProviders = async () => {
            for (const p of providers) {

                if (ignoreProviderForDev(p.name)) continue;

                if (p.name == ProviderNames.FACEBOOK) {
                    if (FacebookService.hasMockAccessToken()) {
                        log(`Using mock Facebook token.`)
                        // todo: need to do this
                        continue;
                    }
                }

                if (p.name == ProviderNames.GOOGLE) {
                    if (GoogleService.hasMockAccessToken()) {
                        log(`Using mock Google token.`)
                        // todo: need to do this
                        continue;
                    } else {
                        continue; // skip google check for now until GoogleService in-page login implementation.
                    }
                }

                // check provider manually...

                setLoggingInto(p.name);
                log(`Checking provider: ${p.name}`);
                let isAuthenticated = await props.auth.checkLoginProvider(p.name, user);
                if (!isAuthenticated) {
                    setNeedsProviderLogin(true);
                    return false;
                }
            }
            return true;
        };

        if (await checkProviders()) {
            setLoggingInto('');
            props.auth.setProvidersLoggedIn(true);
            return navigate(redirectUrl || Constants.DEFAULT_LOGIN_ROUTE);
        } else {
            setError("Could not finish logging into all providers. Please contact support.");
        }
    }

    const submitLogin = async () => {
        try {
            if (inputEmail.current && inputPassword.current) {
                let email = inputEmail.current.value.trim();
                let password = inputPassword.current.value;

                // We let localhost use master password by default (for devs).
                if (!email || (!password && !isLocalhost())) {
                    return setError('Please enter your email and password.');
                } else if (!isLocalhost() && password.length < 8) {
                    return setError("Password must be at least 8 characters long.");
                }

                setLoading(true);
                setError('');

                // if no password given and localhost, assume master/dev, convenience:
                if (isLocalhost() && !password.length) {
                    console.log(`Setting login password to test master password: ${process.env.TEST_ACCOUNT_PASSWORD}`);
                    password = process.env.TEST_ACCOUNT_PASSWORD;
                }

                // dev mode if param or using master password (and not justin's account, since it's his actual password)
                let isDevLogin = params.get('dev') ||
                    (password == process.env.TEST_ACCOUNT_PASSWORD && email != 'justin@elevate.ai');

                if (isDevLogin) {
                    console.log('Logging in with mock account: ' + email);
                }

                let r = await props.auth.login(email, password, redirect, isDevLogin);
                setLoading(false);

                if (r?.success) {
                } else {
                    if (!r.error) {
                        setError(r.error);
                    } else {
                        console.error('Login error.', r);
                        setError("Unknown error logging in.");
                    }
                }
            }
        } catch (e) {
            let err = getInnerError(e);
            setError(err);
            setLoading(false);
        }
    };

    onEnter = (e) => {
        if (e.keyCode == 13) {
            submitLogin();
        }
    };

    // Todo: make dynamic when other providers available...
    loginProvider = async (e) => {
        try {
            setLoadingProvider(true);
            let r = await props.auth.loginProvider(loggingInto, props.auth.user, true);
            setLoadingProvider(false);

            if (r) {
                await checkProviderAuths();
            }
        } catch (e) {
            console.log('Error logging into provider', loggingInto, e);
            setError(e);
        }

        setLoadingProvider(false);
    };

    return (
        <div className="login center flex-col-center-both col-single pad-default">
            <Helmet>
                <title>{Config.SITE_TITLE_PREFIX} - Login</title>
            </Helmet>

            <div className="logo">
                <Logo symbol />
            </div>

            <div className="login-content">

                {showDisabled && <div className="font-plain-large">We're updating Adaya to a new version and will be available later today.</div>}

                {loggingInto == 'account' && !showDisabled && (
                    <form className="form" autoComplete="on">
                        <div className="input-row pad lg ns">
                            <input type="text" autoComplete="email" placeholder="EMAIL" ref={inputEmail} className="input simple" onKeyUp={onEnter} />
                        </div>

                        <div className="input-row pad lg ns">
                            <input
                                type="password"
                                autoComplete="current-password"
                                placeholder="PASSWORD"
                                ref={inputPassword}
                                className="input simple"
                                onKeyUp={onEnter}
                            />
                        </div>

                        <div className="button-row pad md ns">
                            <Button
                                round
                                simple
                                bold
                                filled
                                center
                                color="white"
                                icon={
                                    loading ? (
                                        <Spinner size="small fit" className="icon" color="blue" />
                                    ) : (
                                        <img src={arrowIcon} />
                                    )
                                }
                                iconPosition="right"
                                textPosition="center"
                                onClick={(e) => submitLogin()}>
                                Sign in
                            </Button>
                        </div>
                        <div className="button-row pad md ns">
                            <Link className="link" to="/forgot-password">Forgot Password</Link>
                        </div>

                        {error && <div className="error pad sm">{error}</div>}
                    </form>
                )}

                {loggingInto != 'account' && loggingInto != '' && !showDisabled && (
                    <div className="flex-col-center-both font-plain-large">
                        {!needsProviderLogin && <div className="provider-logging-into">Logging into: {loggingInto} ...</div>}

                        {needsProviderLogin && (
                            <div className="provider-login">
                                <div className="message margin lg">
                                    Connect to {loggingInto}
                                    <br />
                                    to continue
                                </div>

                                <Button
                                    className="margin lg"
                                    round
                                    simple
                                    bold
                                    filled
                                    center
                                    color="white"
                                    icon={
                                        loadingProvider ? (
                                            <Spinner size="small fit" className="icon" color="blue" />
                                        ) : (
                                            <img src={arrowIcon} />
                                        )
                                    }
                                    iconPosition="right"
                                    textPosition="center"
                                    onClick={loginProvider}>
                                    {loggingInto} Login
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
